import React, { useState, useRef, useEffect, type FC } from 'react';
import { Form, Input, Modal } from 'antd';
import * as intl from 'react-intl-universal';
import { Z_INDEX_1030_MODAL } from 'pc-sales/constants/style';
import { verifyMfa } from 'pc-sales/api.v2/auth/oidc';
import { registerMfaModal, resolveMfaVerify, rejectMfaVerify } from 'common/utils/mfa';

let setVisibleFn: (visible: boolean) => void;

registerMfaModal((resolve, reject) => {
    setVisibleFn?.(true);
});

const GlobalMfaModal: FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const hiddenOtpInputRef = useRef<HTMLInputElement>(null);
    const [form] = Form.useForm();

    setVisibleFn = setIsVisible;

    const handleCancel = () => {
        setIsVisible(false);
        form.resetFields();
        rejectMfaVerify(new Error('MFA verification cancelled'));
    };

    const handleOk = async () => {
        try {
            const { otp } = await form.validateFields();
            setIsSubmitting(true);
            await verifyMfa(otp);
            setIsVisible(false);
            form.resetFields();
            resolveMfaVerify();
        } catch (err) {
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (!isVisible) {
            return;
        }

        const syncOtpValue = () => {
            const otp = hiddenOtpInputRef.current?.value;

            if (otp && otp !== form.getFieldValue('otp')) {
                form.setFieldsValue({ otp });
            }
        };

        const hiddenOtpInput = hiddenOtpInputRef.current;

        hiddenOtpInput?.addEventListener('input', syncOtpValue);
        hiddenOtpInput?.addEventListener('change', syncOtpValue);

        return () => {
            hiddenOtpInput?.removeEventListener('input', syncOtpValue);
            hiddenOtpInput?.removeEventListener('change', syncOtpValue);
        };
    }, [form, isVisible]);

    return (
        <Modal
            open={isVisible}
            title="2FA"
            onCancel={handleCancel}
            onOk={handleOk}
            okText={intl.get('Global.Yes')}
            cancelText={intl.get('Global.Cancel')}
            maskClosable={false}
            zIndex={Z_INDEX_1030_MODAL}
            confirmLoading={isSubmitting}
        >
            <Form form={form} layout="vertical">
                <input id="otp" ref={hiddenOtpInputRef} type="hidden" />
                <Form.Item
                    label={intl.get('Component.MFAModalOtpLabel')}
                    name="otp"
                    rules={[
                        { required: true, message: intl.get('Hint.InputSixCode') },
                        { pattern: /^\d{6}$/, message: intl.get('Hint.InputSixCode') },
                    ]}
                >
                    <Input maxLength={6} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default GlobalMfaModal;
